/**
 * Utility functions for retry strategy management
 */

/**
 * Get the effective retry strategy based on the hierarchy:
 * message → campaign → default strategy
 */
export const getEffectiveRetryStrategy = (message, campaign, defaultStrategy) => {
  // Message-level override takes precedence
  if (message?.retry_strategy_id) {
    return message.retry_strategy;
  }
  
  // Campaign-level strategy
  if (campaign?.retry_strategy_id) {
    return campaign.retry_strategy;
  }
  
  // Default strategy
  return defaultStrategy;
};

/**
 * Get the effective max retries based on the hierarchy:
 * message override → campaign override → strategy default
 */
export const getEffectiveMaxRetries = (message, campaign, strategy) => {
  // Message-level override
  if (message?.max_retries !== null && message?.max_retries !== undefined) {
    return message.max_retries;
  }
  
  // Campaign-level override
  if (campaign?.max_retries !== null && campaign?.max_retries !== undefined) {
    return campaign.max_retries;
  }
  
  // Strategy default
  return strategy?.max_attempts || 3;
};

/**
 * Calculate retry delay for a specific attempt number
 */
export const calculateRetryDelay = (strategy, attemptNumber) => {
  if (!strategy) return 30; // Default fallback

  let delay = strategy.base_delay_minutes;
  for (let i = 1; i < attemptNumber; i++) {
    delay *= strategy.backoff_factor;
  }
  return Math.min(delay, strategy.max_delay_minutes);
};

/**
 * Generate retry schedule for a strategy
 */
export const generateRetrySchedule = (strategy, maxRetries = null) => {
  const effectiveMaxRetries = maxRetries || strategy?.max_attempts || 3;
  const schedule = [];

  for (let attempt = 1; attempt <= effectiveMaxRetries; attempt++) {
    schedule.push({
      attempt: attempt,
      delay: calculateRetryDelay(strategy, attempt),
      cumulativeDelay: schedule.reduce((sum, item) => sum + item.delay, 0) + calculateRetryDelay(strategy, attempt)
    });
  }

  return schedule;
};

/**
 * Check if a message can be retried
 */
export const canRetryMessage = (message, strategy) => {
  if (!message || !strategy) return false;

  const maxRetries = getEffectiveMaxRetries(message, null, strategy);
  return message.retry_count < maxRetries;
};

/**
 * Get next retry delay for a message
 */
export const getNextRetryDelay = (message, strategy) => {
  if (!message || !strategy) return null;

  const nextAttempt = message.retry_count + 1;
  return calculateRetryDelay(strategy, nextAttempt);
};

/**
 * Migration utilities for existing retry settings
 */

/**
 * Migrate existing retry settings from old campaign format
 */
export const migrateExistingRetrySettings = (campaign) => {
  const migration = {
    retry_strategy_id: null,
    max_retries: null
  };

  // Migrate journey campaign retry settings
  if (campaign.campaign_type === 'journey' && campaign.journey_schedule) {
    if (campaign.journey_schedule.max_retry_attempts) {
      migration.max_retries = campaign.journey_schedule.max_retry_attempts;
    }
  }

  // Migrate voice campaign retry settings
  if (campaign.channel === 'voice' && campaign.voice_config) {
    if (campaign.voice_config.retry_attempts) {
      migration.max_retries = campaign.voice_config.retry_attempts;
    }
  }

  // Migrate drip campaign retry settings
  if (campaign.campaign_type === 'drip' && campaign.drip_schedule?.message_steps) {
    const voiceSteps = campaign.drip_schedule.message_steps.filter(step => step.voice_config);
    if (voiceSteps.length > 0) {
      const retryAttempts = voiceSteps[0].voice_config.retry_attempts;
      if (retryAttempts) {
        migration.max_retries = retryAttempts;
      }
    }
  }

  return migration;
};

/**
 * Migrate message-level retry settings
 */
export const migrateMessageRetrySettings = (message, campaign) => {
  const migration = {
    retry_strategy_id: null,
    max_retries: null
  };

  // Check for existing retry settings in message
  if (message.retry_attempts) {
    migration.max_retries = message.retry_attempts;
  }

  // Check for voice-specific retry settings
  if (message.voice_config?.retry_attempts) {
    migration.max_retries = message.voice_config.retry_attempts;
  }

  return migration;
};

/**
 * Create a retry strategy from existing settings
 */
export const createStrategyFromExistingSettings = (campaign, name = null) => {
  const strategyName = name || `${campaign.name} Retry Strategy`;
  
  // Extract retry settings from campaign
  let maxAttempts = 3;
  let baseDelay = 30;
  let backoffFactor = 2.0;

  if (campaign.campaign_type === 'journey' && campaign.journey_schedule) {
    maxAttempts = campaign.journey_schedule.max_retry_attempts || 3;
    baseDelay = campaign.journey_schedule.retry_delay_minutes || 30;
  }

  if (campaign.channel === 'voice' && campaign.voice_config) {
    maxAttempts = campaign.voice_config.retry_attempts || 3;
  }

  return {
    name: strategyName,
    description: `Auto-generated strategy from campaign: ${campaign.name}`,
    max_attempts: maxAttempts,
    base_delay_minutes: baseDelay,
    backoff_factor: backoffFactor,
    max_delay_minutes: 1440, // 24 hours
    retry_on_failure: true,
    retry_on_timeout: true,
    retry_on_error: true,
    notify_on_max_retries: false,
    notify_recipients: '',
    is_active: true
  };
};

/**
 * Validation utilities
 */

/**
 * Validate retry strategy data
 */
export const validateRetryStrategy = (data) => {
  const errors = {};

  if (!data.name?.trim()) {
    errors.name = 'Strategy name is required';
  }

  if (data.max_attempts < 1) {
    errors.max_attempts = 'Max attempts must be at least 1';
  }

  if (data.base_delay_minutes < 1) {
    errors.base_delay_minutes = 'Base delay must be at least 1 minute';
  }

  if (data.backoff_factor <= 0) {
    errors.backoff_factor = 'Backoff factor must be greater than 0';
  }

  if (data.max_delay_minutes < data.base_delay_minutes) {
    errors.max_delay_minutes = 'Max delay must be at least the base delay';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Format retry delay for display
 */
export const formatRetryDelay = (minutes) => {
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
    }
  } else {
    const days = Math.floor(minutes / 1440);
    const remainingHours = Math.floor((minutes % 1440) / 60);
    if (remainingHours === 0) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else {
      return `${days} day${days !== 1 ? 's' : ''} ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
    }
  }
};

/**
 * Get retry status for display
 */
export const getRetryStatus = (message, strategy) => {
  if (!message || !strategy) return 'unknown';

  const maxRetries = getEffectiveMaxRetries(message, null, strategy);
  
  if (message.status === 'retry') {
    return 'retrying';
  } else if (message.retry_count >= maxRetries) {
    return 'max_retries_reached';
  } else if (message.retry_count > 0) {
    return 'retried';
  } else {
    return 'no_retries';
  }
};

/**
 * Format retry configuration for backend API
 */
export const formatRetryConfigForBackend = (retryStrategyId, maxRetries) => {
  return {
    retry_strategy_id: retryStrategyId && retryStrategyId !== 'default' ? parseInt(retryStrategyId) : null,
    max_retries: maxRetries ? parseInt(maxRetries) : null
  };
};

export default {
  getEffectiveRetryStrategy,
  getEffectiveMaxRetries,
  calculateRetryDelay,
  generateRetrySchedule,
  canRetryMessage,
  getNextRetryDelay,
  migrateExistingRetrySettings,
  migrateMessageRetrySettings,
  createStrategyFromExistingSettings,
  validateRetryStrategy,
  formatRetryDelay,
  getRetryStatus,
  formatRetryConfigForBackend
}; 